import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { usePermission } from '../composables/usePermission';
import { isChunkLoadError, safeSessionStorage, shouldReloadForChunkError } from './chunk-reload';
import AuthLayout from '../layouts/AuthLayout.vue';
import LoginPage from '../views/LoginPage.vue';
import { useAuthStore } from '../stores/auth';
import {
  buildLoginPath,
  isLoginRoutePath,
  PASSWORD_RESET_APP_PATH,
  resolveDefaultLoginTenantCode,
  resolveLoginRedirectTenantCode,
  stripTenantPrefix,
  tenantPath,
} from './login-route.js';
export {
  appendAdminChildRoutes,
  createAxResourceRoute,
  replaceAdminDashboardRoute,
  type AxResourceRouteDefinition,
  type CreateAxResourceRouteOptions,
} from './admin-route-helpers';
export {
  buildLoginPath,
  defaultLoginPathTenantCode,
  isLoginRoutePath,
  LOGIN_TENANT_CODE_ALL,
  rememberLastLoginTenantCode,
  readLastLoginTenantCode,
  resolveDefaultLoginTenantCode,
  resolveLoginRedirectTenantCode,
  resolveLoginRouteTenantState,
  stripTenantPrefix,
  tenantCodeForLoginRequest,
  tenantPath,
} from './login-route.js';

const PLATFORM_TENANT_CODE = 'platform';

/**
 * 인증 전 진입점(LoginPage / AuthLayout)은 첫 화면이 즉시 그려지도록 eager import.
 *
 * 그 외 인증 후 영역(AdminLayout, Dashboard, 시스템 관리 페이지) 및 에러 페이지는
 * dynamic import 로 분리해 라우트별 청크로 번들링한다.
 *
 * 이렇게 분리하면:
 *   - dev cold-start 에서 /login 만 들어가도 AdminLayout + 모든 SystemXxxPage 가
 *     딸려오는 1,600+ ESM 요청 폭증을 막는다.
 *   - prod 에서는 라우트별 청크가 hashed asset 으로 떨어져 nginx immutable 캐시가
 *     페이지별로 동작한다.
 *
 * vue-router 는 동적 import 청크를 받기 전까지 URL/화면을 바꾸지 않으므로 라우트
 * 전환 시 깜빡임이 발생하지는 않는다 (사내망 기준 50~150ms 추가 지연).
 */
// 명시적 반환 타입(`AsyncComponentLoader`) 을 생략하면 ESLint
// `explicit-module-boundary-types` 가 모든 lazy import 화살표 함수를 잡는다.
// vue-router 의 `RouteRecordRaw.component` 가 동적 import 를 그대로 받으므로
// `() => import(...)` 시그니처면 충분하다.
type AsyncComponentLoader = () => Promise<unknown>;
const AdminLayout: AsyncComponentLoader = () => import('../layouts/AdminLayout.vue');
const DashboardPage: AsyncComponentLoader = () => import('../views/DashboardPage.vue');
const ForbiddenPage: AsyncComponentLoader = () => import('../views/ForbiddenPage.vue');
const NotFoundPage: AsyncComponentLoader = () => import('../views/NotFoundPage.vue');
const SystemCodesPage: AsyncComponentLoader = () => import('../views/system/SystemCodesPage.vue');
const SampleGridPage: AsyncComponentLoader = () => import('../views/samples/SampleGridPage.vue');
const SampleTabGridPage: AsyncComponentLoader = () =>
  import('../views/samples/SampleTabGridPage.vue');
const SampleSplitGridPage: AsyncComponentLoader = () =>
  import('../views/samples/SampleSplitGridPage.vue');
const SampleMasterDetailPage: AsyncComponentLoader = () =>
  import('../views/samples/SampleMasterDetailPage.vue');
const SampleChartPage: AsyncComponentLoader = () => import('../views/samples/SampleChartPage.vue');
const SystemLogsPage: AsyncComponentLoader = () => import('../views/system/SystemLogsPage.vue');
const SystemMenusPage: AsyncComponentLoader = () => import('../views/system/SystemMenusPage.vue');
const SystemRolesPage: AsyncComponentLoader = () => import('../views/system/SystemRolesPage.vue');
const SystemTenantsPage: AsyncComponentLoader = () =>
  import('../views/system/SystemTenantsPage.vue');
const SystemBrandingPage: AsyncComponentLoader = () =>
  import('../views/system/SystemBrandingPage.vue');
const SystemCompanyPage: AsyncComponentLoader = () =>
  import('../views/system/SystemCompanyPage.vue');
const SystemUsersPage: AsyncComponentLoader = () => import('../views/system/SystemUsersPage.vue');
const SystemDocumentTemplatesPage: AsyncComponentLoader = () =>
  import('../views/system/SystemDocumentTemplatesPage.vue');
const AccountSettingsPage: AsyncComponentLoader = () =>
  import('../views/account/AccountSettingsPage.vue');
const PasswordResetPage: AsyncComponentLoader = () =>
  import('../views/account/PasswordResetPage.vue');

/**
 * core-fe 기본 라우트.
 *
 * 패턴: docs/04-authentication.md §8.2.
 * 본 차수에서는 토큰 부재 시 강제 리다이렉트만 placeholder로 구현하고,
 * refresh 자동 호출은 다음 차수에서 추가한다.
 */
export const coreRoutes: RouteRecordRaw[] = [
  {
    path: '/login/:tenantCode',
    component: AuthLayout,
    meta: { requiresAuth: false },
    children: [
      {
        path: '',
        name: 'login',
        component: LoginPage,
        meta: { requiresAuth: false },
      },
    ],
  },
  {
    // 비밀번호 재설정 전용 — 사이드바·메뉴 없이 로그인과 같은 AuthLayout 위에서만 받는다.
    // `/:tenantCode` 의 자식이 아니라 형제여야 AdminLayout(메뉴·채팅 등 부수 호출)이 뜨지 않는다.
    path: `/:tenantCode${PASSWORD_RESET_APP_PATH}`,
    component: AuthLayout,
    meta: { requiresAuth: true, allowMustChangePassword: true },
    children: [
      {
        path: '',
        name: 'account.passwordReset',
        component: PasswordResetPage,
        meta: { requiresAuth: true, allowMustChangePassword: true },
      },
    ],
  },
  {
    path: '/:tenantCode',
    name: 'admin-root',
    component: AdminLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: DashboardPage,
        meta: { requiresAuth: true, menuCode: 'dashboard' },
      },
      // ---------------------------------------------------------------------
      // 시스템 관리 — 메뉴 시드(prisma/seed.ts §PLATFORM_MENUS) 의 path 와 1:1.
      // 라우트는 정적으로 선언하고, 사이드바 노출은 메뉴 API 응답으로 동적 결정.
      // 다음 라운드에서 meta.permission 평가 → 라우터 가드에서 차단을 추가한다.
      // ---------------------------------------------------------------------
      {
        path: 'system/users',
        name: 'system.users',
        component: SystemUsersPage,
        meta: { requiresAuth: true, menuCode: 'system.users', permission: 'system:user:read' },
      },
      {
        path: 'system/roles',
        name: 'system.roles',
        component: SystemRolesPage,
        meta: { requiresAuth: true, menuCode: 'system.roles', permission: 'system:role:read' },
      },
      {
        path: 'system/menus',
        name: 'system.menus',
        component: SystemMenusPage,
        meta: { requiresAuth: true, menuCode: 'system.menus', permission: 'system:menu:read' },
      },
      {
        path: 'system/codes',
        name: 'system.codes',
        component: SystemCodesPage,
        meta: { requiresAuth: true, menuCode: 'system.codes', permission: 'system:code:read' },
      },
      {
        path: 'system/logs',
        name: 'system.logs',
        component: SystemLogsPage,
        meta: { requiresAuth: true, menuCode: 'system.logs', permission: 'system:log:read' },
      },
      {
        path: 'system/tenants',
        name: 'system.tenants',
        component: SystemTenantsPage,
        meta: {
          requiresAuth: true,
          menuCode: 'system.tenants',
          permission: 'system:tenant:read',
          platformOnly: true,
        },
      },
      {
        path: 'system/branding',
        name: 'system.branding',
        component: SystemBrandingPage,
        meta: {
          requiresAuth: true,
          menuCode: 'system.branding',
          permission: 'system:branding:read',
        },
      },
      {
        path: 'system/company',
        name: 'system.company',
        component: SystemCompanyPage,
        meta: {
          requiresAuth: true,
          menuCode: 'system.company',
          permission: 'system:company:read',
        },
      },
      {
        // 문서 서식 관리 (docs/designs/core-document-template-engine.md §7.6.5)
        path: 'system/document-templates',
        name: 'system.document-templates',
        component: SystemDocumentTemplatesPage,
        meta: {
          requiresAuth: true,
          menuCode: 'system.document-templates',
          permission: 'system:document-template:read',
        },
      },
      // -------------------------------------------------------------------
      // 샘플 화면 (R.19) — 그리드/좌우 그리드/마스터-디테일/차트 참조 구현.
      // BE seed(prisma/seed.ts) 의 samples.* code/path 와 1:1.
      // -------------------------------------------------------------------
      {
        path: 'samples/grid',
        name: 'samples.grid',
        component: SampleGridPage,
        meta: { requiresAuth: true, menuCode: 'samples.grid', permission: 'platform:sample:read' },
      },
      {
        path: 'samples/tab-grid',
        name: 'samples.tabGrid',
        component: SampleTabGridPage,
        meta: {
          requiresAuth: true,
          menuCode: 'samples.tabGrid',
          permission: 'platform:sample:read',
        },
      },
      {
        path: 'samples/split-grid',
        name: 'samples.splitGrid',
        component: SampleSplitGridPage,
        meta: {
          requiresAuth: true,
          menuCode: 'samples.splitGrid',
          permission: 'platform:sample:read',
        },
      },
      {
        path: 'samples/master-detail',
        name: 'samples.masterDetail',
        component: SampleMasterDetailPage,
        meta: {
          requiresAuth: true,
          menuCode: 'samples.masterDetail',
          permission: 'platform:sample:read',
        },
      },
      {
        path: 'samples/chart',
        name: 'samples.chart',
        component: SampleChartPage,
        meta: {
          requiresAuth: true,
          menuCode: 'samples.chart',
          permission: 'platform:sample:read',
        },
      },
      // -------------------------------------------------------------------
      // 계정 — 사이드바 메뉴 없음. UserActionMenu 로 진입.
      // (강제 변경 상태는 전용 화면 account.passwordReset 이 받는다.)
      // -------------------------------------------------------------------
      {
        path: 'account/settings',
        name: 'account.settings',
        component: AccountSettingsPage,
        meta: { requiresAuth: true },
      },
      {
        path: 'account/profile',
        redirect: (to) => tenantPath('/account/settings', String(to.params.tenantCode ?? '')),
      },
      {
        path: 'account/change-password',
        redirect: (to) => tenantPath('/account/settings', String(to.params.tenantCode ?? '')),
      },
    ],
  },
  {
    path: '/403',
    name: 'forbidden',
    component: ForbiddenPage,
    meta: { requiresAuth: false },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundPage,
    meta: { requiresAuth: false },
  },
];

/**
 * `/:pathMatch(.*)*` 보다 먼저 평가되도록 Tier 라우트를 삽입한다.
 * (삽입을 마지막에 두면 catch-all 에 흡수되어 추가 경로가 영구 404가 된다.)
 */
function withExtraRoutesBeforeCatchAll(
  base: RouteRecordRaw[],
  extra: RouteRecordRaw[],
): RouteRecordRaw[] {
  if (extra.length === 0) {
    return base;
  }
  const catchAllIdx = base.findIndex(
    (r) => r.path === '/:pathMatch(.*)*' || r.name === 'not-found',
  );
  if (catchAllIdx === -1) {
    return [...base, ...extra];
  }
  return [...base.slice(0, catchAllIdx), ...extra, ...base.slice(catchAllIdx)];
}

export interface CreateAxRouterOptions {
  /** 추가 라우트 (Tier 2/3/4 주입) */
  extraRoutes?: RouteRecordRaw[];
}

export function createAxRouter(
  options: CreateAxRouterOptions = {},
): ReturnType<typeof createRouter> {
  const router = createRouter({
    history: createWebHistory(),
    routes: withExtraRoutesBeforeCatchAll(coreRoutes, options.extraRoutes ?? []),
  });

  // docs/04-authentication.md §8.2 + docs/05-rbac.md §5.2 라우트 가드.
  //
  // 통합 정책 (E.3.9 silent refresh + F.5 permission 평가):
  //   - meta.requiresAuth === false 라우트는 통과 (login/forbidden/not-found).
  //   - access token 부재 + 첫 진입(브라우저 새로고침/탭 재오픈) 이면 한 번
  //     `authStore.refresh()` 를 시도한다 (HttpOnly refresh 쿠키 살아있으면
  //     세션 복원). 이 시도는 앱 부팅 1회로 제한해 무한 재시도를 방지한다.
  //   - refresh 성공: 새 access token + user + permissions 가 store 에 채워진 채 진입.
  //   - refresh 실패: /login 으로 redirect 하고 원래 경로를 ?redirect=... 보존.
  //   - 이미 로그인된 상태로 /login 진입 시 dashboard 로 우회 (UX).
  //   - 인증 통과 후 to.meta.permission 이 정의되어 있으면 usePermission().hasPermission
  //     으로 평가하고 미보유 시 /403 으로 이동.
  let initialRefreshTried = false;

  router.beforeEach(async (to) => {
    const authStore = useAuthStore();
    const mode = import.meta.env.VITE_DEPLOYMENT_MODE === 'on-prem' ? 'on-prem' : ('saas' as const);

    // (0) 공개 라우트 / 로그인 정규화
    if (to.path === '/login') {
      const segment = resolveLoginRedirectTenantCode({
        mode,
        userTenantCode: authStore.user?.tenantCode ?? null,
        defaultTenantCode: resolveDefaultLoginTenantCode(),
      });
      return {
        path: buildLoginPath(segment, {
          redirect: typeof to.query.redirect === 'string' ? to.query.redirect : undefined,
        }),
        replace: true,
      };
    }

    if (isLoginRoutePath(to.path) && authStore.isAuthenticated) {
      return { path: tenantPath('/', authStore.user?.tenantCode ?? '') };
    }

    // 공개 라우트(login/403/portal)는 통과. 단 not-found(catch-all)는 (2)/(3) 의
    // prefix 정규화·로그인 redirect 대상이므로 여기서 통과시키지 않는다.
    if (to.meta.requiresAuth === false && to.name !== 'not-found') {
      return true;
    }

    // (1) 부팅 1회 silent refresh
    if (!authStore.accessToken && !initialRefreshTried) {
      initialRefreshTried = true;
      try {
        await authStore.refresh();
      } catch {
        // 쿠키 없음/만료/revoked → 아래 redirect 분기로 떨어진다.
      }
    }

    // (2) 미인증 → 로그인 redirect (URL 테넌트 살림)
    if (!authStore.accessToken) {
      const urlTenant = routeTenantParam(to.params.tenantCode);
      const segment = resolveLoginRedirectTenantCode({
        mode,
        userTenantCode: null,
        routeTenantCode: urlTenant ?? null,
        defaultTenantCode: resolveDefaultLoginTenantCode(),
      });
      const redirect = to.fullPath !== '/' ? to.fullPath : undefined;
      return buildLoginPath(segment, { redirect });
    }

    // (3) 인증됨 → prefix 정규화
    //   - `/:tenantCode` 하위 매칭(urlTenant 존재): 코드 불일치(타 테넌트 공유 링크)
    //     면 자기 테넌트로 교정.
    //   - urlTenant 미존재: not-found(무-prefix 레거시/북마크) 만 prefix 부여하고,
    //     의도적으로 등록된 top-level 인증 라우트(예: MES `/pop/*`,
    //     `/reports/*/:id/print`)는 prefix 대상이 아니므로 통과시킨다.
    const userTenant = authStore.user?.tenantCode ?? '';
    const urlTenant = routeTenantParam(to.params.tenantCode);
    if (urlTenant !== undefined) {
      if (urlTenant !== userTenant) {
        return { path: tenantPath(stripTenantPrefix(to.fullPath), userTenant), replace: true };
      }
    } else if (to.name === 'not-found') {
      // prefix 부여/제거는 그 결과가 실제 라우트로 매칭될 때만 redirect 한다.
      // 무조건 redirect 하면 여전히 not-found 인 URL(예: `/{tenant}/pop/*` 처럼
      // top-level 전용 라우트에 prefix 가 붙은 경우)에서 prefix 가 무한히 중첩되는
      // 가드 루프에 빠져 탭 전체가 프리즈된다 — 매번 다른 location 이라
      // vue-router 의 무한 redirect 감지에도 걸리지 않는다.
      const prefixed = tenantPath(to.fullPath, userTenant);
      if (router.resolve(prefixed).name !== 'not-found') {
        return { path: prefixed, replace: true };
      }
      const [, firstSegment = ''] = to.path.split('/');
      if (userTenant !== '' && decodeURIComponent(firstSegment) === userTenant) {
        const stripped = stripTenantPrefix(to.fullPath);
        const resolvedStripped = router.resolve(stripped);
        // `/:tenantCode` 는 임의의 단일 세그먼트를 삼키므로, 그 매칭(가짜 테넌트
        // 대시보드)은 제외하고 진짜 top-level 라우트로 이어질 때만 벗겨낸다.
        if (
          resolvedStripped.name !== 'not-found' &&
          resolvedStripped.params.tenantCode === undefined
        ) {
          return { path: stripped, replace: true };
        }
      }
      // 어느 쪽으로도 매칭 불가 → 진짜 404. NotFoundPage 를 그대로 렌더한다.
      return true;
    }

    // (4) mustChangePassword / platformOnly / permission 검사
    if (authStore.user?.mustChangePassword && to.meta.allowMustChangePassword !== true) {
      return { path: tenantPath(PASSWORD_RESET_APP_PATH, userTenant), replace: true };
    }
    // 재설정이 끝난(또는 필요 없는) 사용자가 북마크·뒤로가기로 들어오면 대시보드로.
    if (!authStore.user?.mustChangePassword && to.name === 'account.passwordReset') {
      return { path: tenantPath('/', userTenant), replace: true };
    }

    if (to.meta.platformOnly && authStore.user?.tenantCode !== PLATFORM_TENANT_CODE) {
      return { path: '/403' };
    }

    const required = toPermissionList(to.meta.permission);
    if (required.length > 0) {
      const { hasAllPermissions } = usePermission();
      if (!hasAllPermissions(...required)) {
        return { path: '/403' };
      }
    }

    // (L.ERP.AUDIT.15) `anyPermission` — 하나라도 있으면 통과(OR).
    // 여러 화면을 탭으로 묶은 허브 라우트는 탭마다 권한이 다르다. 단일 키 AND 로 막으면
    // 한쪽 탭 권한만 가진 사용자가 허브 자체에서 403 을 맞는다(페이지는 이미 탭별로 가린다).
    const anyRequired = toPermissionList(to.meta.anyPermission);
    if (anyRequired.length > 0) {
      const { hasAnyPermission } = usePermission();
      if (!hasAnyPermission(...anyRequired)) {
        return { path: '/403' };
      }
    }

    return true;
  });

  // 라우트 청크(dynamic import) 로딩 실패 — 배포 직후 열려 있던 화면에서 메뉴 이동 시.
  //
  // 현재 화면을 reload 하면 누른 메뉴로 가지 못하고 제자리 새로고침만 된다. 이동하려던
  // 주소를 전체 로드해 최신 index.html 로 그 화면을 연다(같은 주소 연속 재시도는 막음).
  router.onError((err, to) => {
    if (!isChunkLoadError(err)) return;
    const href = to ? router.resolve(to.fullPath).href : window.location.href;
    if (shouldReloadForChunkError(href, safeSessionStorage())) {
      window.location.assign(href);
    }
  });

  return router;
}

/**
 * `to.params.tenantCode`(string | string[] | undefined) 를 string|undefined 로 정규화.
 *
 * `/:tenantCode` 하위 매칭일 때만 채워진다. not-found(`/:pathMatch(.*)*`) 로
 * 떨어진 무-prefix 경로는 `undefined` 이며, 가드 (3) 분기 판별에 사용된다.
 */
function routeTenantParam(value: string | string[] | undefined): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === 'string') {
    return value[0];
  }
  return undefined;
}

/**
 * `to.meta.permission` 의 다양한 형태(undefined | string | string[]) 를
 * 일관된 string[] 로 정규화한다. 빈 문자열은 무시.
 */
function toPermissionList(meta: unknown): string[] {
  if (typeof meta === 'string') {
    return meta.length > 0 ? [meta] : [];
  }
  if (Array.isArray(meta)) {
    return meta.filter((c): c is string => typeof c === 'string' && c.length > 0);
  }
  return [];
}
